/**
 * Harbour log form helpers — outcome required, date auto-fills in UK time.
 */

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  HARBOUR_ACTIVITY_TYPE,
  HARBOUR_OUTCOME,
  HARBOUR_TIMEZONE,
  getHarbourFollowUpDaysForOutcome,
  getHarbourIntentLabel,
  getHarbourOutcomeLabel,
  isHarbourIntent,
  isHarbourOutcome,
  isHarbourParkedColumn,
} from 'src/constants/harbour'
import { formatHarbourFollowUpLabel } from 'src/utils/harbourMappers'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * YYYY-MM-DD for a native date input, from the outcome default (1 / 3 / 7).
 * Closed has no date.
 */
export function getHarbourDefaultFollowUpDate(outcome, now = new Date()) {
  const days = getHarbourFollowUpDaysForOutcome(outcome)
  if (days == null) {
    return ''
  }
  return dayjs.tz(now, HARBOUR_TIMEZONE).add(days, 'day').format('YYYY-MM-DD')
}

export function shouldShowHarbourLogFollowUpDate(outcome) {
  return getHarbourFollowUpDaysForOutcome(outcome) != null
}

export function harbourFollowUpDateToIso(
  dateStr,
  timezoneName = HARBOUR_TIMEZONE,
) {
  const value = String(dateStr || '').trim()
  if (!value) {
    return null
  }
  const parsed = dayjs.tz(value, timezoneName)
  return parsed.isValid() ? parsed.startOf('day').toISOString() : null
}

export function formatHarbourFollowUpDateInput(
  dateStr,
  timezoneName = HARBOUR_TIMEZONE,
) {
  const value = String(dateStr || '').trim()
  if (!value) {
    return ''
  }
  const parsed = dayjs.tz(value, timezoneName)
  return parsed.isValid() ? parsed.format('D MMM') : ''
}

/**
 * Save is blocked until they have claimed the venue and picked an outcome.
 * Parked stays off the board until Start work reopens the same card.
 */
export function canSaveHarbourLog({ outcome, alreadyOwned, column } = {}) {
  return (
    Boolean(alreadyOwned) &&
    isHarbourOutcome(outcome) &&
    !isHarbourParkedColumn(column)
  )
}

export function buildHarbourLogBody({
  venueId,
  outcome,
  intent,
  note,
  followUpDate,
} = {}) {
  const body = {
    venueId,
    outcome,
  }

  if (isHarbourIntent(intent)) {
    body.intent = intent
  }

  const trimmedNote = String(note || '').trim()
  if (trimmedNote) {
    body.note = trimmedNote
  }

  if (outcome === HARBOUR_OUTCOME.CLOSED) {
    body.followUpAt = null
    return body
  }

  const iso = harbourFollowUpDateToIso(followUpDate)
  if (iso) {
    body.followUpAt = iso
  }
  return body
}

function stripHarbourLogSummaryPrefix(summary, lastOutcome, lastIntent) {
  const text = String(summary || '').trim()
  if (!text) {
    return ''
  }
  const bits = [getHarbourOutcomeLabel(lastOutcome)]
  if (lastIntent) {
    bits.push(getHarbourIntentLabel(lastIntent))
  }
  const prefix = bits.filter(Boolean).join(' — ')
  if (prefix && text.startsWith(`${prefix} — `)) {
    return text.slice(prefix.length + 3).trim()
  }
  const outcomeLabel = getHarbourOutcomeLabel(lastOutcome)
  if (outcomeLabel && text.startsWith(`${outcomeLabel} — `)) {
    return text.slice(outcomeLabel.length + 3).trim()
  }
  return ''
}

/**
 * Last written CS note. lastActivitySummary can be snooze/park copy, so walk
 * the last outcome_logged activity first.
 */
export function getHarbourLastLogNote(source = {}) {
  const rawNote = typeof source.note === 'string' ? source.note : ''
  const explicit = String(source.lastNote || rawNote || '').trim()
  if (explicit) {
    return explicit
  }

  const entries = Array.isArray(source.activity) ? source.activity : []
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i]
    if (entry?.type !== HARBOUR_ACTIVITY_TYPE.OUTCOME_LOGGED) {
      continue
    }
    const fromMeta = String(entry?.metadata?.note || '').trim()
    if (fromMeta) {
      return fromMeta
    }
    return stripHarbourLogSummaryPrefix(
      entry.summary,
      entry.metadata?.outcome || source.lastOutcome,
      entry.metadata?.intent || source.lastIntent,
    )
  }

  return stripHarbourLogSummaryPrefix(
    source.lastActivitySummary,
    source.lastOutcome,
    source.lastIntent,
  )
}

/**
 * Recap for the drawer after save / on GET reopen. Null until they have
 * logged an outcome — claim-only should still look blank.
 */
export function getHarbourLastLogMemory(source = {}, now = new Date()) {
  const lastOutcome = source.lastOutcome || source.outcome || null
  if (!isHarbourOutcome(lastOutcome)) {
    return null
  }

  const lastIntent = source.lastIntent || source.intent || null
  const nextFollowUpAt =
    lastOutcome === HARBOUR_OUTCOME.CLOSED
      ? null
      : source.nextFollowUpAt || harbourFollowUpDateToIso(source.followUpDate)
  const followUpLabel =
    lastOutcome === HARBOUR_OUTCOME.CLOSED
      ? 'No follow-up'
      : source.nextFollowUpLabel ||
        formatHarbourFollowUpDateInput(source.followUpDate) ||
        formatHarbourFollowUpLabel(nextFollowUpAt, now) ||
        ''

  return {
    outcome: lastOutcome,
    outcomeLabel: getHarbourOutcomeLabel(lastOutcome),
    intent: isHarbourIntent(lastIntent) ? lastIntent : null,
    intentLabel: isHarbourIntent(lastIntent)
      ? getHarbourIntentLabel(lastIntent)
      : '',
    note: getHarbourLastLogNote({ ...source, lastOutcome, lastIntent }),
    nextFollowUpAt: nextFollowUpAt || null,
    followUpLabel,
    followUpCaption:
      lastOutcome === HARBOUR_OUTCOME.CLOSED
        ? 'No follow-up'
        : followUpLabel
        ? `Follow-up ${followUpLabel}`
        : '',
  }
}
